import React from "react";

function formatInlineContent(
  content: string,
  lineIndex: number,
  baseIndex: number,
): React.ReactNode {
  if (!content) return content;

  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let urlMatch;
  let matchIndex = 0;

  while ((urlMatch = urlRegex.exec(content)) !== null) {
    // Add text before the URL
    if (urlMatch.index > lastIndex) {
      parts.push(content.substring(lastIndex, urlMatch.index));
    }

    // Add the URL as a link
    const url = urlMatch[0];
    const href = url.startsWith("http") ? url : `https://${url}`;
    parts.push(
      <a
        key={`inline-${lineIndex}-${baseIndex}-${matchIndex++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-aiBlue-600 hover:text-aiBlue-700 underline"
      >
        {url}
      </a>,
    );

    lastIndex = urlMatch.index + urlMatch[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts.length > 0 ? parts : content;
}

/**
 * Converts text with basic markdown and HTML into React elements
 * Supports:
 * - **text** or <b>text</b> or <strong>text</strong> -> bold (including multiline **...**)
 * - *text* or <i>text</i> or <em>text</em> -> italic
 * - <br/> or <br> -> line break
 * - URLs (http://, https://, www.) -> clickable links
 * - Line breaks (\n)
 */
export function formatMarkdown(text: string | any): React.ReactNode {
  if (!text) return null;
  const textStr = typeof text === "string" ? text : String(text);
  if (!textStr) return null;

  // Extract **...** blocks (including multiline) so they render as bold after we split by \n
  const boldBlocks: string[] = [];
  const withBoldPlaceholders = textStr.replace(
    /\*\*([\s\S]*?)\*\*/g,
    (_, content: string) => {
      boldBlocks.push(content);
      return `__BOLD_${boldBlocks.length - 1}__`;
    },
  );

  const textWithBreaks = withBoldPlaceholders.replace(/<br\s*\/?>/gi, "\n");

  const lines = textWithBreaks.split("\n");

  return lines.map((line, lineIndex) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Regex for URLs (http://, https://, www.)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    // Regex for markdown (**text** or *text*)
    const markdownRegex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)/g;
    // Regex for HTML formatting tags (<b>, <strong>, <i>, <em>)
    const htmlBoldRegex = /<(b|strong)>(.+?)<\/(b|strong)>/gi;
    const htmlItalicRegex = /<(i|em)>(.+?)<\/(i|em)>/gi;

    // Collect all matches
    const urlMatches: Array<{ index: number; length: number; url: string }> =
      [];
    let urlMatch;
    while ((urlMatch = urlRegex.exec(line)) !== null) {
      urlMatches.push({
        index: urlMatch.index,
        length: urlMatch[0].length,
        url: urlMatch[0],
      });
    }

    const markdownMatches: Array<{
      index: number;
      length: number;
      type: "bold" | "italic";
      content: string;
    }> = [];
    let markdownMatch;
    while ((markdownMatch = markdownRegex.exec(line)) !== null) {
      if (markdownMatch[1]) {
        markdownMatches.push({
          index: markdownMatch.index,
          length: markdownMatch[0].length,
          type: "bold",
          content: markdownMatch[2],
        });
      } else if (markdownMatch[3]) {
        markdownMatches.push({
          index: markdownMatch.index,
          length: markdownMatch[0].length,
          type: "italic",
          content: markdownMatch[4],
        });
      }
    }

    // Collect HTML tag matches
    const htmlMatches: Array<{
      index: number;
      length: number;
      type: "bold" | "italic";
      content: string;
    }> = [];

    // Process <b> and <strong> tags
    let htmlBoldMatch;
    htmlBoldRegex.lastIndex = 0;
    while ((htmlBoldMatch = htmlBoldRegex.exec(line)) !== null) {
      htmlMatches.push({
        index: htmlBoldMatch.index,
        length: htmlBoldMatch[0].length,
        type: "bold",
        content: htmlBoldMatch[2] || "",
      });
    }

    // Process <i> and <em> tags
    let htmlItalicMatch;
    htmlItalicRegex.lastIndex = 0;
    while ((htmlItalicMatch = htmlItalicRegex.exec(line)) !== null) {
      htmlMatches.push({
        index: htmlItalicMatch.index,
        length: htmlItalicMatch[0].length,
        type: "italic",
        content: htmlItalicMatch[2] || "",
      });
    }

    // Placeholders for **...** multiline bold (from pre-pass)
    const boldPlaceholderRegex = /__BOLD_(\d+)__/g;
    const boldPlaceholderMatches: Array<{
      index: number;
      length: number;
      type: "bold_placeholder";
      content: string;
    }> = [];
    let boldPhMatch;
    while ((boldPhMatch = boldPlaceholderRegex.exec(line)) !== null) {
      const idx = parseInt(boldPhMatch[1], 10);
      if (idx >= 0 && idx < boldBlocks.length) {
        boldPlaceholderMatches.push({
          index: boldPhMatch.index,
          length: boldPhMatch[0].length,
          type: "bold_placeholder",
          content: boldBlocks[idx],
        });
      }
    }

    // Combine and sort all matches by position
    const allMatches = [
      ...urlMatches.map((m) => ({ ...m, type: "url" as const })),
      ...markdownMatches.map((m) => ({ ...m, type: m.type })),
      ...htmlMatches.map((m) => ({ ...m, type: m.type })),
      ...boldPlaceholderMatches,
    ].sort((a, b) => a.index - b.index);

    // Remove overlapping matches (prioritizes HTML > Markdown > URLs)
    const filteredMatches: typeof allMatches = [];
    for (let i = 0; i < allMatches.length; i++) {
      const match = allMatches[i];
      let shouldInclude = true;

      // Check for overlap with previous matches
      for (let j = 0; j < filteredMatches.length; j++) {
        const prevMatch = filteredMatches[j];
        const matchEnd = match.index + match.length;
        const prevMatchEnd = prevMatch.index + prevMatch.length;

        // If there's overlap
        if (
          (match.index >= prevMatch.index && match.index < prevMatchEnd) ||
          (matchEnd > prevMatch.index && matchEnd <= prevMatchEnd) ||
          (match.index <= prevMatch.index && matchEnd >= prevMatchEnd)
        ) {
          // Priority: HTML > Markdown > URLs (bold_placeholder same as bold)
          const priority: Record<string, number> = {
            bold: 3,
            italic: 3,
            bold_placeholder: 3,
            url: 1,
          };
          const matchPriority = priority[match.type] ?? 2;
          const prevPriority = priority[prevMatch.type] ?? 2;

          if (matchPriority > prevPriority) {
            // Remove previous match if current has higher priority
            filteredMatches.splice(j, 1);
            break;
          } else if (matchPriority < prevPriority) {
            // Don't include current match if previous has higher priority
            shouldInclude = false;
            break;
          } else {
            // Same priority, don't include current
            shouldInclude = false;
            break;
          }
        }
      }

      if (shouldInclude) {
        filteredMatches.push(match);
      }
    }

    // Process each match
    filteredMatches.forEach((match) => {
      // Add text before the match
      if (match.index > lastIndex) {
        const textBefore = line.substring(lastIndex, match.index);
        if (textBefore) {
          parts.push(textBefore);
        }
      }

      // Process match based on type
      if (match.type === "url") {
        const url = match.url;
        const href = url.startsWith("http") ? url : `https://${url}`;
        parts.push(
          <a
            key={`${lineIndex}-${match.index}-url`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-aiBlue-600 hover:text-aiBlue-700 underline"
          >
            {url}
          </a>,
        );
      } else if (match.type === "bold" || match.type === "bold_placeholder") {
        // Process tag content to support URLs; bold_placeholder may contain \n
        const content = match.content;
        const linesInBold = content.split("\n");
        const processedContent =
          linesInBold.length === 1
            ? formatInlineContent(content, lineIndex, match.index)
            : linesInBold.map((subLine, subIdx) => (
                <React.Fragment key={`${lineIndex}-${match.index}-${subIdx}`}>
                  {formatInlineContent(
                    subLine,
                    lineIndex,
                    match.index + subIdx,
                  )}
                  {subIdx < linesInBold.length - 1 && <br />}
                </React.Fragment>
              ));
        parts.push(
          <strong key={`${lineIndex}-${match.index}-bold`}>
            {processedContent}
          </strong>,
        );
      } else if (match.type === "italic") {
        // Process tag content to support URLs
        const content = match.content;
        const processedContent = formatInlineContent(
          content,
          lineIndex,
          match.index,
        );
        parts.push(
          <em key={`${lineIndex}-${match.index}-italic`}>
            {processedContent}
          </em>,
        );
      }

      lastIndex = match.index + match.length;
    });

    // Add remaining text after the last match
    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex));
    }

    // If line is empty, render only line break
    if (parts.length === 0 && lineIndex < lines.length - 1) {
      return <br key={`line-${lineIndex}`} />;
    }

    // Return line with line break (except for the last one)
    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {parts.length > 0 ? parts : null}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}
